// Drains whatever's queued in consultationOfflineStore back to Supabase.
// Always attempts the real network call rather than trusting navigator.onLine
// (which is an optimistic hint, not a guarantee) — failures just leave rows
// unsynced for the next attempt.

import { supabase } from './supabaseClient';
import {
  getUnsyncedSessions, markSessionSynced, getSessionLocal,
  getUnsyncedDocuments, markDocumentSynced,
} from './consultationOfflineStore';

let syncing = false;
const listeners = new Set();

// fn receives the current total count of un-synced sessions + documents.
export function onSyncStatusChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Session-level columns that mirror a shared_data key of the same name —
// dirtySharedKeys (tracked in ManassasConsultationWizard) uses these same
// names, so one dirty set gates both the shared_data merge and these columns.
const SESSION_SHARED_COLUMNS = ['client_name', 'client_email', 'client_phone', 'service_address', 'service_city', 'service_state', 'service_zip'];

// Two devices can have this same session/document open at once (staff
// supporting staff is an explicit, intentional use case here — see the
// no-hard-locking decision). A blind full-row upsert would let whoever syncs
// last silently erase whatever the other device just saved. Instead: fetch
// the server's current row and merge onto it, only overwriting the specific
// fields THIS device actually edited (dirtySharedKeys/dirtyFieldKeys) —
// everything else is left exactly as the server already has it. Status,
// delivery/link, and completion columns are handled by their own direct
// targeted updates (submitFinal, copyRemoteLink) and are never touched here.
async function syncSessionRow(session) {
  const { id, office_code, created_by, created_at, shared_data, dirtySharedKeys } = session;

  const { data: serverRow } = await supabase.from('consultation_sessions').select('*').eq('id', id).single();

  const payload = { id };
  if (!serverRow) {
    // Nothing to merge onto yet — this device's snapshot is the whole story.
    payload.office_code = office_code;
    payload.created_by = created_by;
    payload.created_at = created_at;
    payload.status = session.status || 'draft';
    payload.shared_data = shared_data || {};
    for (const col of SESSION_SHARED_COLUMNS) payload[col] = session[col] ?? (col === 'service_state' ? 'VA' : null);
  } else {
    // Undefined (not just an empty array) means this local record predates
    // dirty-key tracking — fall back to treating every locally-known key as
    // dirty rather than silently dropping a genuine in-flight edit.
    const dirty = Array.isArray(dirtySharedKeys) ? new Set(dirtySharedKeys) : new Set(Object.keys(shared_data || {}));
    const mergedSharedData = { ...(serverRow.shared_data || {}) };
    dirty.forEach(key => { mergedSharedData[key] = (shared_data || {})[key]; });
    payload.shared_data = mergedSharedData;
    for (const col of SESSION_SHARED_COLUMNS) {
      if (dirty.has(col)) payload[col] = session[col] ?? null;
    }
  }

  const { error } = await supabase.from('consultation_sessions').upsert(payload, { onConflict: 'id' });
  if (error) throw error;
  await markSessionSynced(id);
}

async function syncDocumentRow(doc) {
  const { sessionId, templateKey, pdfBlob, fieldValues, status, signedAt, dirtyFieldKeys } = doc;
  let pdfPath = doc.pdfPath || null;
  if (pdfBlob) {
    pdfPath = `sessions/${sessionId}/${templateKey}.pdf`;
    const { error: uploadError } = await supabase.storage
      .from('consultation-documents')
      .upload(pdfPath, pdfBlob, { contentType: 'application/pdf', upsert: true });
    if (uploadError) throw uploadError;
  }

  const { data: serverDoc } = await supabase.from('consultation_documents')
    .select('*').eq('session_id', sessionId).eq('template_key', templateKey).single();

  const payload = { session_id: sessionId, template_key: templateKey, updated_at: new Date().toISOString() };
  if (!serverDoc) {
    payload.field_values = fieldValues;
    payload.status = status;
    payload.pdf_path = pdfPath;
    payload.signed_at = signedAt;
  } else {
    const dirty = Array.isArray(dirtyFieldKeys) ? new Set(dirtyFieldKeys) : new Set(Object.keys(fieldValues || {}));
    const mergedFieldValues = { ...(serverDoc.field_values || {}) };
    dirty.forEach(key => { mergedFieldValues[key] = (fieldValues || {})[key]; });
    payload.field_values = mergedFieldValues;

    // Status only ever moves forward (pending -> signed) — never let a
    // stale local copy revert a document another device already completed.
    const statusRank = { pending: 0, signed: 1 };
    payload.status = (statusRank[status] ?? 0) >= (statusRank[serverDoc.status] ?? 0) ? status : serverDoc.status;

    // Don't blank out a pdf_path/signed_at the server already has from a
    // more-complete sync elsewhere when this call isn't uploading a fresh PDF.
    payload.pdf_path = pdfPath || serverDoc.pdf_path;
    payload.signed_at = signedAt || serverDoc.signed_at;
  }

  const { error } = await supabase.from('consultation_documents').upsert(payload, { onConflict: 'session_id,template_key' });
  if (error) throw error;
  await markDocumentSynced(sessionId, templateKey);
}

export async function syncPendingConsultations() {
  if (syncing) return; // in-flight guard — avoid double-uploading a multi-MB PDF on overlapping triggers
  syncing = true;
  try {
    for (const session of await getUnsyncedSessions()) {
      try {
        await syncSessionRow(session);
      } catch (err) {
        console.error('Consultation sync: session failed', session.id, err);
      }
    }

    for (const doc of await getUnsyncedDocuments()) {
      // consultation_documents.session_id is a FK — the session row must exist
      // server-side before a document referencing it can sync.
      const parentSession = await getSessionLocal(doc.sessionId);
      if (parentSession && !parentSession.synced) continue;
      try {
        await syncDocumentRow(doc);
      } catch (err) {
        console.error('Consultation sync: document failed', doc.sessionId, doc.templateKey, err);
      }
    }
  } finally {
    syncing = false;
    const pendingCount = (await getUnsyncedSessions()).length + (await getUnsyncedDocuments()).length;
    listeners.forEach(fn => fn(pendingCount));
  }
}

let intervalHandle = null;

// iOS has no Background Sync API — sync only ever runs while the app is
// actually open, so these triggers (not just the online event) matter.
export function startAutoSync() {
  syncPendingConsultations();
  window.addEventListener('online', syncPendingConsultations);
  if (!intervalHandle) {
    intervalHandle = setInterval(syncPendingConsultations, 45000);
  }
}

export function stopAutoSync() {
  window.removeEventListener('online', syncPendingConsultations);
  if (intervalHandle) {
    clearInterval(intervalHandle);
    intervalHandle = null;
  }
}
